// Hidden admin sign-in page. Only reachable at /admin/login. Not linked from
// any public page and marked noindex/nofollow at the App-level.
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowRight, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth, formatApiError } from "@/lib/adminAuth";

export default function AdminLogin() {
    const { login, user, checking } = useAdminAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    // If already signed in, bounce to the dashboard
    useEffect(() => {
        if (user) navigate(location.state?.from || "/admin", { replace: true });
    }, [user, location.state, navigate]);

    if (checking) return null;
    if (user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const u = await login(email.trim().toLowerCase(), password);
            toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
            navigate(u.must_change_password ? "/admin/change-password" : (location.state?.from || "/admin"), { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0b1f22] text-ivory flex items-center justify-center px-6 py-12 relative overflow-hidden">
            {/* Ambient gold blur */}
            <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-gold/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[420px] h-[420px] bg-jade/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none" />

            <div className="w-full max-w-md relative">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-gold">
                        <Shield size={12} /> Serenity Cove · Staff
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl text-ivory mt-4">Admin Sign In</h1>
                    <p className="text-ivory/60 text-sm mt-3">Staff access only — authorized personnel with a Serenity Cove admin account.</p>
                </div>

                <form onSubmit={submit} className="bg-white/[0.04] border border-white/10 backdrop-blur-md rounded-2xl p-7 md:p-8 shadow-2xl" data-testid="admin-login-form">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-ivory/60">Email</span>
                        <div className="mt-2 flex items-center gap-3 border border-white/15 rounded-lg px-4 py-3 bg-black/20 focus-within:border-gold/50 transition-colors">
                            <Mail size={15} className="text-ivory/40" />
                            <input
                                type="email"
                                required
                                autoComplete="username"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="bg-transparent outline-none w-full text-ivory placeholder:text-ivory/30 text-sm"
                                placeholder="you@serenitycoveresorts.com"
                                data-testid="admin-login-email"
                            />
                        </div>
                    </label>

                    <label className="block mt-5">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-ivory/60">Password</span>
                        <div className="mt-2 flex items-center gap-3 border border-white/15 rounded-lg px-4 py-3 bg-black/20 focus-within:border-gold/50 transition-colors">
                            <Lock size={15} className="text-ivory/40" />
                            <input
                                type="password"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="bg-transparent outline-none w-full text-ivory placeholder:text-ivory/30 text-sm"
                                placeholder="••••••••"
                                data-testid="admin-login-password"
                            />
                        </div>
                    </label>

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="admin-login-submit"
                        className="mt-7 w-full bg-gold text-navy px-6 py-3.5 rounded-full text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-gold/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                        {busy ? "Signing in…" : (<>Sign In <ArrowRight size={13} /></>)}
                    </button>

                    <div className="mt-6 flex items-center justify-between text-[11px]">
                        <Link to="/admin/forgot-password" className="text-ivory/60 hover:text-gold transition-colors">Forgot password?</Link>
                        <Link to="/" className="text-ivory/40 hover:text-ivory transition-colors">← Public site</Link>
                    </div>
                </form>
                <p className="mt-6 text-center text-[10px] uppercase tracking-[0.22em] text-ivory/30">Session expires after 30 min of inactivity.</p>
            </div>
        </div>
    );
}
